import { useEffect, useState } from "react";
import usePricing from "./usePricing";

const useCalculator = () => {
  const {
    country,
    products,
    setCountry,
    updateData,
    getOriginalPricing,
    resetAllProducts,
  } = usePricing();

  const [state, setState] = useState({
    monthly: 0,
    annual: 0,
    savings: 0,
    discount: 15,
    premiumSupportPrice: 0,
  });

  const [enabledProducts, setEnabledProducts] = useState(
    products.filter((product) => product.default_enabled)
  );

  useEffect(() => {
    const allProducts = resetAllProducts();

    setEnabledProducts(
      allProducts[country].filter((product) => product.default_enabled)
    );
  }, [country]);

  useEffect(() => {
    calculateSubscription();
  }, [products, enabledProducts, state.discount]);

  const setDiscount = (discount) => {
    setState((state) => ({
      ...state,
      discount,
    }));
  };

  const isEnabled = (p) => {
    const idx = enabledProducts.findIndex((product) => {
      return product.id === p.id;
    });
    return idx > -1;
  };

  const getProduct = (id) => {
    return products.find((p) => p.id === id);
  };

  const enableProduct = (product) => {
    if (product.id && (product.base || product.per_license)) {
      let newProduct;

      if (product.id === "fleet_management") {
        // Enable Fleet Tracking instead when Fleet Management is enabled
        const fleetTrackingProduct = products.find(
          (e) => e.id === "fleet_tracking"
        );
        newProduct = fleetTrackingProduct;
      } else {
        newProduct = product;
      }
      newProduct.license_count = undefined; // Don't change to 0

      setEnabledProducts((enabledProducts) => [
        ...enabledProducts.filter((e) => e.id !== product.id),
        newProduct,
      ]);
    }
  };

  const disableProduct = (product) => {
    setEnabledProducts((enabledProducts) =>
      enabledProducts.filter((e) => e.id !== product.id)
    );
  };

  const updateLicenseCount = (productId, count) => {
    setEnabledProducts((enabledProducts) => {
      const products = [...enabledProducts];
      const index = products.findIndex((p) => p.id === productId);
      products[index].license_count = count;
      return products;
    });
  };

  const calculateSubscription = () => {
    let monthlyTotal = 0;
    let monthlyDiscountedTotal = 0;
    let annualTotal = 0;
    let annualSavings = 0;

    // Handle products except premium support
    // prettier-ignore
    enabledProducts
      .filter(
        (product) =>
          product.id !== "premium_support" &&
          !product.hidden &&
          (product.license_count != undefined || !product.per_license)
      )
      .forEach((product) => {
        const productLicenseCount = product.license_count || product.min_licenses || 0;
        const monthlyCost = product.base + product.per_license * productLicenseCount;
        const monthlyCostDiscounted = product.has_annual_discount ? (monthlyCost * (100 - state.discount)) / 100 : monthlyCost;
        const annualCost = monthlyCost * 12;
        const annualDiscount = product.has_annual_discount ? (annualCost * state.discount) / 100 : 0;

        monthlyTotal += monthlyCost;
        monthlyDiscountedTotal += monthlyCostDiscounted;
        annualTotal += annualCost - annualDiscount;
        annualSavings += annualDiscount;
      });

    // Handle premium support
    let premiumSupportPrice = 0;
    const isPremiumSupportEnabled = !!enabledProducts.find(
      (e) => e.id === "premium_support"
    );
    if (isPremiumSupportEnabled) {
      let annualPremiumSupportPrice = 795;

      const fsmProduct = enabledProducts.find((e) => e.id === "fsm");
      if (fsmProduct) {
        const licenseCount = fsmProduct.license_count;

        // Premium support pricing is based on tiers
        if (licenseCount >= 6 && licenseCount <= 14) {
          annualPremiumSupportPrice = 999;
        } else if (licenseCount >= 15 && licenseCount <= 30) {
          annualPremiumSupportPrice = 1500;
        } else if (licenseCount >= 31 && licenseCount <= 50) {
          annualPremiumSupportPrice = 3000;
        } else if (licenseCount >= 51) {
          annualPremiumSupportPrice = 5000;
        }
      }

      premiumSupportPrice = annualPremiumSupportPrice / 12;
      monthlyTotal += premiumSupportPrice;
      monthlyDiscountedTotal += premiumSupportPrice;
      annualTotal += annualPremiumSupportPrice;
    }

    setState((state) => ({
      ...state,
      monthly: monthlyTotal,
      monthlyDiscounted: monthlyDiscountedTotal,
      annual: annualTotal,
      savings: annualSavings,
      premiumSupportPrice,
    }));
  };

  return {
    country,
    products,
    setCountry,
    setDiscount,
    isEnabled,
    getProduct,
    enableProduct,
    disableProduct,
    updateData,
    getOriginalPricing,
    updateLicenseCount,
    ...state,
  };
};

export default useCalculator;
